import { FC, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { isStandaloneAriaShell } from '../utils/ariaUrls';
import { loadUserFromStorage } from '../utils/authStorage';
import { collectPortalUsage, shouldSendPortalUsageEvent } from '../services/portalUsageCollector';
import type {
  PortalUsagePlatform,
  PortalUsageProductContext,
  PortalUsageSurface,
} from '../types/portalUsage';

function resolvePlatform(): PortalUsagePlatform {
  if (Capacitor.isNativePlatform()) {
    const p = Capacitor.getPlatform();
    if (p === 'ios') return 'native_ios';
    if (p === 'android') return 'native_android';
  }
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const webIsIOS = /iPhone|iPad|iPod/i.test(ua);
  const webIsAndroid = /Android/i.test(ua);
  const narrow = typeof window !== 'undefined' && window.innerWidth <= 768;
  if (narrow || webIsIOS || webIsAndroid) return 'web_mobile';
  return 'web_desktop';
}

function resolveProductContext(raw: unknown): PortalUsageProductContext {
  if (raw === 'aria_chat' || raw === 'aptive_index' || raw === 'both') {
    return raw;
  }
  return 'aptive_index';
}

function resolveSurface(): PortalUsageSurface {
  return isStandaloneAriaShell() ? 'aria_standalone' : 'portal';
}

const SKIP_PATH_PREFIXES = [
  '/login',
  '/aria/login',
  '/password-forget',
  '/reset-forget-password',
  '/login-by-token',
  '/assessment',
];

/**
 * Increments server rollup counters on route / ?tab= changes. Must render under BrowserRouter.
 */
const RouteUsageTracker: FC = () => {
  const location = useLocation();

  const tab = useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search);
      const t = sp.get('tab');
      return t && t.trim() ? t.trim() : null;
    } catch {
      return null;
    }
  }, [location.search]);

  useEffect(() => {
    const stored = loadUserFromStorage();
    if (!stored?.token) return;

    const path = location.pathname || '/';
    for (const prefix of SKIP_PATH_PREFIXES) {
      if (path === prefix || path.startsWith(`${prefix}/`)) return;
    }

    const dedupeKey = `${path}|${tab ?? ''}`;
    if (!shouldSendPortalUsageEvent(dedupeKey)) return;

    const productContext = resolveProductContext(stored.user?.platformContext);
    collectPortalUsage({
      routePath: path,
      tab,
      platform: resolvePlatform(),
      surface: resolveSurface(),
      productContext,
    });
  }, [location.pathname, location.search, tab]);

  return null;
};

export default RouteUsageTracker;
